import { GiftIcon } from "lucide-react"
import moment from "moment"
import { Session } from "next-auth"
import Link from "next/link"
import { AnchorHTMLAttributes } from "react"

import { technos } from "@/data/technos"

import { cn } from "@/lib/utils"

import BoilerplateCardIcon from "./boilerplate-card-icon"
import Price from "./price"
import { BoilerplateWithVotes } from "./sections/boilerplate-list"
import Avatar from "./ui/avatar"
import { Card, CardDescription, CardTitle } from "./ui/card"
import VerifiedCard from "./verified-card"
import VoteBtn from "./vote-btn"

export default async function BoilerplateCard({
  boilerplate,
  session,
  displayVoteBtn = true,
  className,
}: {
  boilerplate: BoilerplateWithVotes
  session: Session | null
  displayVoteBtn?: boolean
  className?: string
}) {
  return (
    <div className={cn("flex w-full gap-1 sm:gap-2")}>
      <BoilerplateCardContent className={className} boilerplate={boilerplate} />
      {displayVoteBtn ? (
        <>
          <VoteBtn
            size="md"
            className="sm:hidden"
            boilerplate={boilerplate}
            session={session}
          />
          <VoteBtn
            className="hidden sm:block"
            boilerplate={boilerplate}
            session={session}
          />
        </>
      ) : (
        <></>
      )}
    </div>
  )
}
;[]

export function BoilerplateCardContent({
  boilerplate,
  avatarSize = "sm",
  ...props
}: {
  boilerplate: Partial<BoilerplateWithVotes>
  avatarSize?: "sm" | "md" | "lg"
} & AnchorHTMLAttributes<HTMLAnchorElement>) {
  return (
    <Link
      {...props}
      className={cn("w-full max-w-3xl")}
      href={`/boilerplates/${boilerplate.id}`}
    >
      <Card className={cn("w-full shrink-0 grow", props.className)}>
        <div className="flex w-full shrink-0 gap-4">
          <Avatar
            className="hidden sm:block"
            size={avatarSize}
            src={boilerplate.avatar ?? ""}
            alt={`${boilerplate.title} avatar`}
          />
          <div className="flex flex-col gap-1">
            <div className="flex flex-wrap gap-2">
              <CardTitle>{boilerplate.title}</CardTitle>
              <VerifiedCard
                verified={boilerplate.verified || boilerplate.free || false}
              />
              <Price boilerplate={boilerplate} size="sm" />
              {boilerplate?.couponDiscount ? (
                <div className="flex h-fit w-fit items-center gap-1 rounded-md bg-orange-200 px-2 py-0.5 text-sm font-medium text-orange-700">
                  <GiftIcon />
                  <p>
                    {boilerplate?.couponDiscount} OFF (
                    {moment().endOf("month").diff(moment(), "days")} days left)
                  </p>
                </div>
              ) : (
                <></>
              )}
            </div>
            <CardDescription>{boilerplate.description}</CardDescription>
            <div className="mt-3 flex grow flex-col">
              <div className="flex flex-wrap gap-1">
                {technos.map(({ name, icon: Icon }, i) => {
                  if (!boilerplate.technos?.includes(name)) return <></>
                  return (
                    <BoilerplateCardIcon
                      key={`techno-${name}`}
                      icon={<Icon className="h-6 w-6 shrink-0" />}
                      name={name}
                    />
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      </Card>
    </Link>
  )
}
